import { useState } from 'react'
import { deactivateUser, reactivateUser } from '@/api/users'
import { getApiErrorMessage } from '@/lib/apiError'
import { ROLE_LABELS } from '@/lib/users'
import { Button } from '@/components/ui/button'
import { UserStatusBadge } from '@/components/UserStatusBadge'
import { formatDate } from '@/lib/format'
import type { User } from '@/api/types'

function getDeactivateConfirmMessage(name: string): string {
  return `Vill du inaktivera ${name}? Personen förlorar åtkomst direkt och kan bara komma åt kontot igen om det återaktiveras.`
}

interface UserTableRowProps {
  user: User
  currentUserId: number
  onEdit: (user: User) => void
  onChanged: (user: User) => void
}

export function UserTableRow({ user, currentUserId, onEdit, onChanged }: UserTableRowProps): React.ReactElement {
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const isSelf = user.id === currentUserId

  const handleDeactivate = async (): Promise<void> => {
    if (!window.confirm(getDeactivateConfirmMessage(user.display_name))) return

    setErrorMessage(null)
    setIsProcessing(true)

    try {
      await deactivateUser(user.id)
      onChanged({ ...user, active: false })
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'Kunde inte inaktivera användaren just nu.'))
    } finally {
      setIsProcessing(false)
    }
  }

  const handleReactivate = async (): Promise<void> => {
    setErrorMessage(null)
    setIsProcessing(true)

    try {
      const updated = await reactivateUser(user.id)
      onChanged(updated)
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'Kunde inte återaktivera användaren just nu.'))
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <tr className="border-t border-border/70">
      <td className="px-3 py-4 font-medium">{user.display_name}</td>
      <td className="px-3 py-4 text-muted-foreground">{user.email}</td>
      <td className="px-3 py-4 text-muted-foreground">{ROLE_LABELS[user.role]}</td>
      <td className="px-3 py-4">
        <UserStatusBadge active={user.active} />
      </td>
      <td className="px-3 py-4 text-muted-foreground">{formatDate(user.created_at)}</td>
      <td className="py-4 pl-3 pr-6">
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-none"
              onClick={() => onEdit(user)}
            >
              Redigera
            </Button>
            {user.active && !isSelf && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-none"
                disabled={isProcessing}
                onClick={() => void handleDeactivate()}
              >
                Inaktivera
              </Button>
            )}
            {!user.active && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-none"
                disabled={isProcessing}
                onClick={() => void handleReactivate()}
              >
                Aktivera
              </Button>
            )}
          </div>
          {errorMessage && <p className="text-xs text-destructive">{errorMessage}</p>}
        </div>
      </td>
    </tr>
  )
}
